import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import DOMPurify from "dompurify";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import { Post } from "../../types/Post";
import { useAuth } from "../../context/AuthContext";
import { usePost } from "../../context/PostContext";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { convert } from "html-to-text";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

interface PostCardProps {
  post: Post;
}

const PostCard: React.FC<PostCardProps> = ({ post }) => {
  const { userToken } = useAuth();
  const { deletePostById, handleSetSelectedPost } = usePost();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const canAdmin = userToken?.user.profiles?.some((profile) => {
    return profile.id === post.authorProfileId || profile.realtor?.teamId === post.idRealtorTeam
  })

  const sanitizedContent = DOMPurify.sanitize(post.postText)

  // Función para obtener el número de teléfono disponible
  const getAvailablePhoneNumber = (): string | null => {
    if (post.userPhoneNumber && post.userPhoneNumber.trim() !== "") {
      return post.userPhoneNumber
    } else if (post.realtorPhone && post.realtorPhone.trim() !== "") {
      return post.realtorPhone
    }
    return null
  }

  // Función para abrir WhatsApp con el número disponible
  const openWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation()
    const phoneNumber = getAvailablePhoneNumber()

    if (phoneNumber) {
      const formattedNumber = phoneNumber.replace(/\D/g, "")
      const postTitle = post.postText ? convert(post.postText).substring(0, 50) + "..." : ""
      const message = encodeURIComponent(`Estoy interesado en trabajar en el post: ${postTitle}`)
      window.open(`https://api.whatsapp.com/send?phone=${formattedNumber}&text=${message}`, "_blank")
    } else {
      Swal.fire({
        title: "Error",
        text: "No hay número de teléfono disponible",
        icon: "error",
        confirmButtonColor: "#d33",
      })
    }
  }

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleSetSelectedPost(post); 
    navigate("/posts/edit-post"); 
    handleMenuClose(new MouseEvent("click") as unknown as React.MouseEvent);
  };

  const handleDelete = async () => {
    handleMenuClose(new MouseEvent("click") as unknown as React.MouseEvent); 
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción eliminará por completo el post.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await deletePostById(post.id); 
        Swal.fire({
          title: 'Éxito',
          text: 'El post ha sido eliminado correctamente.',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: 'Hubo un problema al eliminar el post. Por favor, intente nuevamente.',
          icon: 'error',
          confirmButtonColor: '#d33'
        });
      }
      handleMenuClose(new MouseEvent("click") as unknown as React.MouseEvent); 
    }
  };

  const getContactInfo = () => {
    const isBroker = post.postType === "Oportunidad";
    const contactName = post.realtorFullName || post.userFullName || "Mira esta publicación";
    const contactPhone = post.realtorPhone || post.userPhoneNumber|| "Sin teléfono";
    const emoji = isBroker ? "💼" : "👤";
  
    return `${emoji} ${contactName} (${isBroker ? "Broker" : "Cliente"})\n📞 ${contactPhone}`;
  };
  


  const handleShare = () => {
    handleMenuClose(new MouseEvent("click") as unknown as React.MouseEvent); 
    const postContact = getContactInfo();

    const postText = convert(post.postText); 
  
    const shareMessage = `${postText}\n\n${postContact}`;
  
    Swal.fire({
      title: "Compartir publicación",
      html: `
        <div style="display: flex; justify-content: center; gap: 10px;">
          <a id="twitter-share" style="cursor: pointer;">
            <img src="https://cdn-icons-png.flaticon.com/512/124/124021.png" width="40" />
          </a>
          <a id="whatsapp-share" style="cursor: pointer;">
            <img src="https://cdn-icons-png.flaticon.com/512/124/124034.png" width="40" />
          </a>
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: "Cancelar",
      showConfirmButton: false,
      didOpen: () => {
        document.getElementById("twitter-share")?.addEventListener("click", () => {
          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`, "_blank");
        });
        document.getElementById("whatsapp-share")?.addEventListener("click", () => {
          window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareMessage)}`, "_blank");
        });
      }
    });
  };

  return (
    <>
      {/* Card del post */}
      <Card
        sx={{
          maxWidth: 600,
          height: 400,
          display: "flex",
          flexDirection: "column",
          margin: "auto",
          marginBottom: 2,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          backgroundColor: "#fafafa",
          transition: "box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out",
          "&:hover": {
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.2)",
            transform: "translateY(-4px)",
          },
        }}
        onClick={handleClickOpen}
      >
        {/* Header con la foto, nombre y fecha */}
        <CardHeader
          avatar={
            <Avatar
              src={post.realtorProfileImage || post.userProfileImage || ""}
              alt={post.realtorFullName || post.userFullName || ""}
              sx={{ width: 60, height: 60, border: "2px solid #28a745" }}
            />
          }
          title={
            <Box>
              <Typography fontWeight="bold" variant="h6" sx={{ fontSize: 18, color: "#333",   display: "-webkit-box", 
                  WebkitBoxOrient: "vertical", 
                  WebkitLineClamp: 2, 
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {post.realtorFullName || post.userFullName}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                {post.postType === "Oportunidad" ? "Broker" : "Cliente"}
              </Typography>
            </Box>
          }
          subheader={
            <Typography variant="body2" color="textSecondary">
              {new Date(post.createdAt).toLocaleDateString("es-ES", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </Typography>
          }
          action={
            <>
              <IconButton onClick={openWhatsApp} sx={{ color: "#25D366", mr: 1 }}>
                <WhatsAppIcon />
              </IconButton>
              <IconButton onClick={handleMenuOpen} sx={{ color: "#666" }}>
                <MoreHorizIcon />
              </IconButton>
            </>
          }
        />

        {/* Menú desplegable */}
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
        >
          {canAdmin && (
            <>
              <MenuItem onClick={handleEdit}>Editar</MenuItem>
              <MenuItem onClick={handleDelete}>Eliminar</MenuItem>
            </>
          )}
          <MenuItem onClick={handleShare}>Compartir</MenuItem>
        </Menu>

        {/* Información de contacto */}
        <Box
          sx={{
            px: 3,
            pt: 2,
            pb: 2,
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "#f5f5f5",
          }}
        >
          <ContactMailIcon sx={{ color: "#28a745" }} />
          <Typography variant="body1" color="textPrimary">
            <strong>Contacto:</strong> {post.contactName} - {post.contactType}
          </Typography>
        </Box>

        {/* Contenido del post */}
        <CardContent
          sx={{
            flexGrow: 1,
            overflow: "hidden",
            px: 3,
            pt: 2,
            position: "relative",
          }}
        >
          <Typography
            variant="body1"
            component="div"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            sx={{
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 7,
              overflow: "hidden",
              lineHeight: 1.6,
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "40px",
                background: "linear-gradient(to bottom, transparent, #fafafa)",
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Diálogo para ver el post completo */}
      <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>
          {post.realtorFullName || post.userFullName} - Creado el{" "}
          {new Date(post.createdAt).toLocaleDateString("es-ES", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </DialogTitle>
        <DialogContent>
          {/* Información de contacto en el diálogo */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              <strong>Nombre de contacto:</strong> {post.contactName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Tipo de contacto:</strong> {post.contactType}
            </Typography>
            {getAvailablePhoneNumber() && (
              <Typography variant="body2" color="textSecondary">
                <strong>Teléfono:</strong> {getAvailablePhoneNumber()}
              </Typography>
            )}
          </Box>
          {/* Contenido completo del post */}
          <Typography variant="body1" component="div" dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
        </DialogContent>
        <DialogActions sx={{ display: "flex", justifyContent: "space-between", px: 3 }}>
          <Button
            startIcon={<WhatsAppIcon />}
            onClick={openWhatsApp}
            variant="contained"
            sx={{
              bgcolor: "#25D366",
              color: "white",
              "&:hover": {
                bgcolor: "#128C7E",
              },
            }}
          >
            Contactar por WhatsApp
          </Button>
          <Button onClick={handleClose} color="primary" sx={{ color: "#28a745" }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PostCard
